import React, { useState, useMemo, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useClienteData } from '../../hooks/useClienteData';
import { useEquipoActions } from '../../hooks/useEquipoActions';
import { filtrarClientesPorBusqueda, abrirWhatsApp, sedesQueCoinciden } from '../../utils/clienteUtils';
import { estadoCliente } from '../../utils/clienteEstado';
import { leerOferta, mensajeOferta, ofrecerPorWhatsApp } from '../../utils/ofertaMantenimiento';
import ActionSheet from '../ui/ActionSheet';
import { M } from '../servicio/ServicioUI';
import { toTitleCase } from '../../utils/titleCase';
import { LuTriangleAlert, LuMessageCircle, LuPhone, LuWrench, LuShoppingCart, LuFolderOpen } from 'react-icons/lu';
import BusquedaBar from '../ui/BusquedaBar';
import { CONTENEDOR, PantallaHeader, BotonPrimario, Segmentado, PAGINA, BotonFlotante } from '../ui/Pantalla';
import ClienteCard        from './ClienteCard';
import ClienteRow, { GRID_CLIENTES } from './ClienteRow';
import ClienteForm        from './ClienteForm';
import CrearClienteModal  from './CrearClienteModal';
import SedeModal          from '../SedeModal';
import EquipoModal        from '../EquipoModal';
import Paginacion         from '../ui/Paginacion';
import { POR_PAGINA } from '../../utils/paginacion';



export default function ClienteManager({ onNuevoServicio, onNuevaVenta, abrirCrearDirecto = false, onCrearConsumido }) {
    const { clientes, sedes, equipos, servicios, cargarDatos } = useClienteData();
    const { handleArchivar, handleRestaurar, handleEliminarDefinitivo } = useEquipoActions(cargarDatos);

    const [busqueda, setBusqueda]               = useState('');
    const [sedeFoco, setSedeFoco]               = useState(null);   // abrir el cliente en esa sede (28-oct-2026)
    const [pagina, setPagina]                   = useState(1);
    const [modalOpen, setModalOpen]             = useState(null);
    // "+ Cliente" desde el Panel: abre directo el alta
    useEffect(() => {
        if (abrirCrearDirecto) { setModalOpen('nuevo'); onCrearConsumido && onCrearConsumido(); }
    }, [abrirCrearDirecto, onCrearConsumido]);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [selectedEquipo, setSelectedEquipo]   = useState(null);
    const [expandedId, setExpandedId]           = useState(null);
    const [confirmEliminarCliente, setConfirmEliminarCliente] = useState(null);
    const [nombreConfirmar, setNombreConfirmar] = useState('');
    const [form, setForm] = useState({
        id: null, nombre: '', calle: '', numero: '', piso: '', depto: '',
        localidad: '', provincia: 'Buenos Aires', telefono: '', cuilDni: '',
        notas: '', condicionIva: 'CONSUMIDOR_FINAL', clienteTipo: 'PARTICULAR'
    });

    // Lista de Clientes (13-oct-2026): mismo idioma que Trabajos y Mapa. Chips para filtrar,
    // semáforo del último service, lo pendiente y un próximo paso. Primero lo que pide atención.
    const [orden, setOrden] = useState('atender');
    const [chip, setChip] = useState('todos');
    const [menu, setMenu] = useState(null);
    const estados = useMemo(() => Object.fromEntries(clientes.map(c => [c.id, estadoCliente(c, sedes, equipos, servicios)])),
        [clientes, sedes, equipos, servicios]);
    const CHIPS = [
        { id: 'todos', label: 'Todos', pasa: () => true },
        { id: 'EMPRESA', label: 'Empresas', pasa: e => e.esEmpresa },
        { id: 'PARTICULAR', label: 'Particulares', pasa: e => !e.esEmpresa },
    ];
    // 2-nov-2026: resumen arriba que también filtra (antes eran chips de alerta y dos columnas casi vacías)
    const RESUMEN = [
        { id: 'deben', label: 'Para cobrar', pasa: e => e.deuda > 0, color: 'var(--etapa-cobrar)' },
        { id: 'vencido', label: 'Service vencido', pasa: e => e.deuda <= 0 && e.sem === 'rojo', color: '#DC2626' },
        { id: 'porVencer', label: 'Por vencer: ofrecer', pasa: e => e.deuda <= 0 && e.sem === 'amarillo', color: '#D48800' },
        { id: 'alDia', label: 'Al día', pasa: e => e.deuda <= 0 && e.sem !== 'rojo' && e.sem !== 'amarillo', color: null },
    ];
    const GRUPO = { 0: 'Para cobrar', 1: 'Service vencido', 2: 'Por vencer', 3: 'Al día' };
    const enBusqueda = filtrarClientesPorBusqueda(clientes, sedes, equipos, busqueda);
    const [resumen, setResumen] = useState(null);   // id de RESUMEN elegido, o null
    const pasaTipo = CHIPS.find(c => c.id === chip)?.pasa || (() => true);
    const pasaResumen = RESUMEN.find(r => r.id === resumen)?.pasa || (() => true);
    const pasaChip = (e) => pasaTipo(e) && pasaResumen(e);
    const delTipo = enBusqueda.filter(c => estados[c.id] && pasaTipo(estados[c.id]));
    const porNombre = (a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es');
    const filtrados = enBusqueda.filter(c => estados[c.id] && pasaChip(estados[c.id])).sort((a, b) => {
        const ea = estados[a.id], eb = estados[b.id];
        if (orden === 'az') return porNombre(a, b);
        if (orden === 'visita') return String(eb.ultimo || '').localeCompare(String(ea.ultimo || '')) || porNombre(a, b);
        // Atender primero: me deben (más plata arriba) → vencidos (el más viejo arriba) → por vencer → el resto
        return ea.prioridad - eb.prioridad
            || (ea.prioridad === 0 ? eb.deuda - ea.deuda : 0)
            || (ea.prioridad <= 2 ? String(ea.ultimo || '').localeCompare(String(eb.ultimo || '')) : String(eb.ultimo || '').localeCompare(String(ea.ultimo || '')))
            || porNombre(a, b);
    });
    const deudaTotal = filtrados.reduce((t, c) => t + (estados[c.id]?.deuda || 0), 0);

    const accionar = (cliente, accion) => {
        // 20-oct-2026: mismo mensaje y descuento que el Radar, y queda registrado "Ofrecido"
        if (accion === 'ofrecer') {
            leerOferta().then(oferta => ofrecerPorWhatsApp({
                telefono: cliente.telefono, clienteId: cliente.id, tipo: 'SANITIZACION', origen: 'RADAR',
                mensaje: mensajeOferta({ cliente: cliente.nombre, tipo: 'SANITIZACION', fechaUltimo: estados[cliente.id]?.ultimo, oferta }),
            }));
        }
        if (accion === 'cobrar') {
            // A Trabajos → Por cobrar, ya buscando a este cliente
            try { sessionStorage.setItem('trabajosAbrir', JSON.stringify({ grupo: 'cobrar', busqueda: cliente.nombre })); } catch { /* */ }
            window.dispatchEvent(new CustomEvent('ir-a-seccion', { detail: 'trabajos' }));
        }
    };
    const totalPaginas   = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
    const paginaActual   = Math.min(pagina, totalPaginas);
    const clientesPagina = useMemo(() =>
        filtrados.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA),
        [filtrados, paginaActual]
    );

    const irA = (n) => setPagina(Math.max(1, Math.min(n, totalPaginas)));

    const handleGuardarCliente = async (e) => {
        e.preventDefault();
        const loading = toast.loading('Guardando...');
        try {
            await api.put(`/clientes/${form.id}`, {
                ...form,
                nombre:   toTitleCase(form.nombre),
                clienteTipo:  form.clienteTipo  || 'PARTICULAR',
                condicionIva: form.condicionIva || 'CONSUMIDOR_FINAL',
                calle:    toTitleCase(form.calle) || 'Sin dirección',
                numero:   form.numero?.trim()   || '0',
                localidad: toTitleCase(form.localidad) || 'Sin localidad',
                provincia: form.provincia?.trim() || 'Buenos Aires',
            });
            toast.success('Cliente actualizado', { id: loading });
            setModalOpen(null);
            cargarDatos();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'Error al guardar', { id: loading });
        }
    };

    const handleEliminarCliente = (id) => { setNombreConfirmar(''); setConfirmEliminarCliente(id); };
    // 1-nov-2026: borrar un cliente con varios lugares o una empresa pide escribir el nombre (y lo controla el backend)
    const aBorrar = clientes.find(c => c.id === confirmEliminarCliente) || null;
    const sedesABorrar = aBorrar ? sedes.filter(s => (s.cliente?.id ?? s.clienteId) === aBorrar.id).length : 0;
    const equiposABorrar = aBorrar ? equipos.filter(e => sedes.some(s => s.id === (e.sede?.id ?? e.sedeId) && (s.cliente?.id ?? s.clienteId) === aBorrar.id)).length : 0;
    const pideNombre = !!aBorrar && (aBorrar.tieneTarifa || sedesABorrar > 1);
    const nombreOk = !pideNombre || nombreConfirmar.trim().toLowerCase() === String(aBorrar?.nombre || '').trim().toLowerCase();

    const confirmarEliminarCliente = async () => {
        const id = confirmEliminarCliente;
        if (!nombreOk) return;
        setConfirmEliminarCliente(null);
        try {
            await api.delete(`/clientes/${id}`, { params: pideNombre ? { confirmar: nombreConfirmar.trim() } : {} });
            toast.success('Eliminado');
            cargarDatos();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'Error al eliminar'); }
    };

    return (
        <div className={PAGINA}>

            <div className={CONTENEDOR}>
                <PantallaHeader titulo="Clientes" subtitulo="Clientes, sedes y equipos"
                    busqueda={<BusquedaBar valor={busqueda} onChange={v => { setBusqueda(v); setPagina(1); }} placeholder="Cliente, sede, teléfono, S/N…" />}
                    accion={<BotonPrimario onClick={() => setModalOpen('nuevo')}>Nuevo</BotonPrimario>} />

                {/* Chips (mismo estilo que Trabajos) + orden */}
                <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 sin-barra">
                    {CHIPS.map(c => {
                        const n = c.id === 'todos' ? enBusqueda.length : enBusqueda.filter(x => estados[x.id] && c.pasa(estados[x.id])).length;
                        const activo = chip === c.id;
                        if (c.alerta && !n) return null;
                        return (
                            <button key={c.id} type="button" onClick={() => { setChip(c.id); setPagina(1); }} aria-pressed={activo}
                                className={`h-8 md:h-10 px-3 md:px-3.5 rounded-full shrink-0 inline-flex items-center gap-2 text-label font-bold border transition-all active:scale-95 ${activo ? 'border-brand-red text-ink bg-[rgba(232,66,47,0.10)]' : 'border-black/10 dark:border-white/10 text-secondary'}`}>
                                {c.alerta && <span className="w-2 h-2 rounded-full bg-[color:var(--alerta)]" />}
                                {c.label}<span className="text-muted">{n}</span>
                            </button>
                        );
                    })}
                </div>
                {/* Resumen: cuántos hay para cobrar, vencidos, por vencer y al día. Tocar filtra (otra vez, saca el filtro) */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {RESUMEN.map(r => {
                        const lista = delTipo.filter(c => r.pasa(estados[c.id]));
                        const plata = r.id === 'deben' ? lista.reduce((t, c) => t + (estados[c.id]?.deuda || 0), 0) : 0;
                        const activo = resumen === r.id;
                        return (
                            <button key={r.id} type="button" onClick={() => { setResumen(activo ? null : r.id); setPagina(1); }} aria-pressed={activo}
                                className={`text-left px-3.5 py-2.5 rounded-2xl border-2 active:scale-[0.98] transition-all ${activo ? 'border-brand-red bg-[rgba(232,66,47,0.08)]' : 'border-black/[0.07] dark:border-white/[0.08] bg-card'}`}>
                                <span className="block text-body-lg font-black leading-tight" style={r.color && lista.length ? { color: r.color } : undefined}>
                                    {lista.length}{plata > 0 && <span className="text-ink"> · <M valor={plata} /></span>}
                                </span>
                                <span className="block text-caption font-bold text-secondary">{r.label}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between gap-2">
                    <Segmentado opciones={[{ id: 'atender', label: 'Atender primero' }, { id: 'visita', label: 'Último service' }, { id: 'az', label: 'A-Z' }]}
                        valor={orden} onChange={id => { setOrden(id); setPagina(1); }} />
                    <span className="text-caption text-muted text-right">
                        {resumen === 'deben' && deudaTotal > 0 ? <>Te deben <M valor={deudaTotal} className="font-black text-ink" /> · </> : null}
                        {filtrados.length} cliente{filtrados.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* Lista: tarjeta en celu, fila en compu (ClienteRow arma las dos) */}
                <div className="space-y-2">
                    {clientesPagina.length > 0 && (
                        <div className={`hidden md:grid ${GRID_CLIENTES} gap-4 px-4 text-label font-bold uppercase tracking-wider text-muted`}>
                            <span>Cliente</span><span>Último service</span><span className="text-right">Qué hacer</span><span />
                        </div>
                    )}
                    {clientesPagina.map((cliente, i) => (
                        <React.Fragment key={cliente.id}>
                        {/* Atender primero: un título por grupo (para cobrar, vencido, por vencer, al día) */}
                        {orden === 'atender' && !resumen && (i === 0 || estados[clientesPagina[i - 1].id]?.prioridad !== estados[cliente.id]?.prioridad) && (
                            <p className="px-1 pt-2 text-label font-black uppercase tracking-widest text-muted">{GRUPO[estados[cliente.id]?.prioridad]}</p>
                        )}
                        <ClienteRow cliente={cliente} e={estados[cliente.id]}
                            sedesMatch={sedesQueCoinciden(cliente, sedes, busqueda)}
                            onAbrirSede={(s) => { setSedeFoco(s.id); setExpandedId(cliente.id); }}
                            onAbrir={() => { setSedeFoco(null); setExpandedId(cliente.id); }}
                            onAccion={(acc) => accionar(cliente, acc)}
                            onMenu={() => setMenu(cliente)} />
                        </React.Fragment>
                    ))}
                </div>

            {/* Panel overlay del cliente expandido */}
            {expandedId && (() => {
                const cliente = clientes.find(c => c.id === expandedId);
                if (!cliente) return null;
                return (
                    // 22-oct-2026: en compu el panel se centraba con translate, y eso hacía que las
                    // ventanas que abre adentro (cierre mensual, historial, confirmar) quedaran
                    // encerradas en el panel: cortadas arriba, corridas y con el fondo a medias.
                    // Ahora el fondo es el que centra (flex), sin transform.
                    <div className="fixed inset-0 z-[41] bg-black/50 flex items-end md:items-center justify-center md:p-6 md:pl-[calc(var(--modal-sb,0px)+1.5rem)]"
                        onClick={() => setExpandedId(null)}>
                        <div onClick={e => e.stopPropagation()}
                            className="w-full max-h-[calc(var(--vh,1vh)*85)] overflow-y-auto rounded-t-3xl bg-card shadow-2xl md:max-w-2xl md:max-h-[calc(var(--vh,1vh)*80)] md:rounded-3xl">
                            <div className="sticky top-0 z-10 bg-card px-5 pt-4 pb-3 border-b border-black/[0.07] dark:border-white/[0.07]">
                                <div className="w-10 h-1 rounded-full mx-auto mb-3 bg-chip md:hidden" />
                                <div className="flex items-center justify-between">
                                    <h3 className="text-title font-black text-ink">{cliente.nombre}</h3>
                                    <button onClick={() => setExpandedId(null)}
                                        className="w-9 h-9 rounded-xl flex items-center justify-center text-muted bg-chip active:scale-90">✕</button>
                                </div>
                            </div>
                            <div className="p-5">
                                <ClienteCard
                                    cliente={cliente} sedes={sedes} equipos={equipos} servicios={servicios}
                                    isExpanded={true}
                                    onToggleExpand={() => setExpandedId(null)}
                                    onEditCliente={(c) => { setExpandedId(null); setForm({ ...c, id: c.id }); setModalOpen('editar'); }}
                                    onDeleteCliente={(id) => { setExpandedId(null); handleEliminarCliente(id); }}
                                    onEditEquipo={(eq, c) => { setExpandedId(null); setSelectedEquipo(eq); setSelectedCliente(c); setModalOpen('equipo'); }}
                                    onArchivarEquipo={handleArchivar}
                                    onRestaurarEquipo={handleRestaurar}
                                    onEliminarEquipoDefinitivo={handleEliminarDefinitivo}
                                    onAddSede={(c) => { setExpandedId(null); setSelectedCliente(c); setModalOpen('sede'); }}
                                    onAddEquipo={(c) => { setExpandedId(null); setSelectedCliente(c); setSelectedEquipo(null); setModalOpen('equipo'); }}
                                    onNuevoServicio={onNuevoServicio}
                                    onNuevaVenta={onNuevaVenta}
                                    enModal={true}
                                    sedeFoco={sedeFoco} busqueda={busqueda}
                                />
                            </div>
                        </div>
                    </div>
                );
            })()}

            {filtrados.length === 0 && (
                <div className="py-12 px-6 text-center rounded-2xl border border-dashed border-black/10 dark:border-white/10 space-y-4">
                    <p className="text-body text-muted">{busqueda ? 'Ningún cliente coincide con la búsqueda' : 'Todavía no hay clientes'}</p>
                    <button onClick={() => setModalOpen('nuevo')}
                        className="h-11 px-5 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95">+ Nuevo cliente</button>
                </div>
            )}

            <Paginacion pagina={paginaActual} totalPaginas={totalPaginas}
                irA={irA} next={() => irA(paginaActual + 1)} prev={() => irA(paginaActual - 1)} />
            </div>{/* cierre max-w-6xl */}

            {/* FAB "+" (celular) — mismo lugar que en Trabajos, Venta y Productos */}
            <BotonFlotante onClick={() => setModalOpen('nuevo')} label="Nuevo cliente" />

            {/* MODALES */}
            <CrearClienteModal isOpen={modalOpen === 'nuevo'} onClose={() => setModalOpen(null)}
                onClienteCreado={() => { setModalOpen(null); cargarDatos(); }} />

            {modalOpen === 'editar' && (
                <ClienteForm form={form} setForm={setForm} errors={{}}
                    onSubmit={handleGuardarCliente} onClose={() => setModalOpen(null)} />
            )}

            {modalOpen === 'sede' && selectedCliente && (
                <SedeModal cliente={selectedCliente}
                    sedes={sedes.filter(s => s.cliente?.id === selectedCliente.id)}
                    onRefresh={cargarDatos} onClose={() => setModalOpen(null)} />
            )}

            {modalOpen === 'equipo' && selectedCliente && (
                <EquipoModal cliente={selectedCliente}
                    sedes={sedes.filter(s => s.cliente?.id === selectedCliente.id)}
                    equipos={equipos.filter(eq => {
                        const ids = sedes.filter(s => s.cliente?.id === selectedCliente.id).map(s => s.id);
                        return ids.includes(eq.sedeId);
                    })}
                    equipoParaEditar={selectedEquipo}
                    onRefresh={() => { cargarDatos(); setSelectedEquipo(null); }}
                    onClose={() => { setModalOpen(null); setSelectedEquipo(null); }} />
            )}

            {/* ⋯ de cada cliente: contacto y atajos (antes el WhatsApp verde iba en cada fila) */}
            <ActionSheet open={!!menu} onClose={() => setMenu(null)}>
                {menu && (() => {
                    const e = estados[menu.id];
                    const item = 'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-body font-bold text-ink active:bg-chip text-left';
                    const cerrar = (fn) => () => { setMenu(null); fn(); };
                    return (
                        <>
                            <p className="px-4 pt-1 pb-2 text-label font-black uppercase tracking-widest text-muted truncate">{menu.nombre}</p>
                            <button className={item} onClick={cerrar(() => setExpandedId(menu.id))}><LuFolderOpen size={18} /> Abrir ficha</button>
                            {menu.telefono && <button className={item} onClick={cerrar(() => abrirWhatsApp(menu.telefono, menu.nombre))}><LuMessageCircle size={18} className="text-[#25D366]" /> WhatsApp</button>}
                            {menu.telefono && <a className={item} href={`tel:${String(menu.telefono).replace(/[^\d+]/g, '')}`} onClick={() => setMenu(null)}><LuPhone size={18} /> Llamar</a>}
                            {menu.telefono && e?.sem !== 'verde' && e?.accion !== 'ofrecer' && <button className={item} onClick={cerrar(() => accionar(menu, 'ofrecer'))}><LuMessageCircle size={18} /> Ofrecer service</button>}
                            {onNuevoServicio && <button className={item} onClick={cerrar(() => onNuevoServicio(menu))}><LuWrench size={18} /> Nuevo trabajo</button>}
                            {onNuevaVenta && <button className={item} onClick={cerrar(() => onNuevaVenta(menu))}><LuShoppingCart size={18} /> Nueva venta</button>}
                        </>
                    );
                })()}
            </ActionSheet>

            {/* Modal confirmación eliminar cliente */}
            {confirmEliminarCliente && (
                <>
                    <div className="fixed inset-0 bg-black/70 z-[1999] backdrop-blur-sm" />
                    <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                        <div className="bg-card rounded-3xl w-full max-w-sm border border-[#D13A28]/30 shadow-2xl p-6">
                            <div className="text-center mb-5">
                                <p className="mb-2 flex justify-center"><LuTriangleAlert size={32} /></p>
                                <h3 className="text-title font-black text-ink uppercase">Eliminar cliente</h3>
                            </div>
                            <p className="text-caption text-secondary text-center mb-4 leading-snug">
                                Se elimina <b className="text-ink">{aBorrar?.nombre || 'el cliente'}</b> entero: {sedesABorrar} lugar{sedesABorrar !== 1 ? 'es' : ''}, {equiposABorrar} equipo{equiposABorrar !== 1 ? 's' : ''} y todos sus trabajos. No se puede deshacer.
                            </p>
                            {pideNombre && (
                                <label className="block mb-4">
                                    <span className="block mb-1.5 text-caption font-bold text-ink">Para confirmar, escribí <b>{aBorrar?.nombre}</b></span>
                                    <input value={nombreConfirmar} onChange={e => setNombreConfirmar(e.target.value)} autoFocus
                                        className="w-full h-11 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none border border-black/10 dark:border-white/10" />
                                </label>
                            )}
                            <div className="flex gap-2">
                                <button onClick={() => setConfirmEliminarCliente(null)}
                                    className="flex-1 py-3 rounded-2xl font-black text-label uppercase bg-chip text-secondary active:scale-95">
                                    Cancelar
                                </button>
                                <button onClick={confirmarEliminarCliente} disabled={!nombreOk}
                                    className="flex-[2] py-3 rounded-2xl font-black text-label uppercase text-white bg-brand-red active:scale-95 disabled:opacity-40">
                                    Sí, eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
